import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import { apiClient } from '../../../api/apiClient';
import { btn, primaryBtn, tabGroupStyle, tabBtn } from '../usermgmt/styles';
import {
  wrapStyle, noticeStyle, bodyStyle, departmentPanelStyle, panelTitleStyle, departmentButtonStyle, selectedDepartmentButtonStyle, contentStyle, sectionStyle, sectionHeadStyle, sectionTitleStyle, optionGridStyle, optionStyle, countStyle,
} from './initialAssignmentModalStyles';

function targetName(target) {
  return target?.name || target?.company_name || target?.code || '';
}

function targetType(target) {
  return target?.target_type === 'group' ? 'group' : 'company';
}
function useSelectedSet(selectedCodes) {
  return useMemo(() => new Set(selectedCodes || []), [selectedCodes]);
}

function buildDefaultsUrl(target, departmentName) {
  const params = new URLSearchParams({
    target_type: targetType(target),
    target_code: target.code,
  });
  if (departmentName) params.set('department_name', departmentName);
  return `/api/admin/initial-assignment-defaults?${params.toString()}`;
}

// [4대보험 | 3.3%] 탭(2026-11-03 합치기-2 결정 7a) — 3.3% 탭 = 이 회사 테넌트의 3.3% 부서 묶음(group FL_<테넌트>).
//   묶음 코드는 서버가 알려준다(GET /api/departments?kind=freelancer → group_code). 못 받으면 탭을 안 그린다(종전 화면).
//   3.3% 탭 값은 테넌트 안 모든 회사의 3.3% 등록에 똑같이 붙는다.
export default function InitialAssignmentDefaultsModal({
  open,
  onClose,
  target: companyTarget,
  showToast,
}) {
  const [tab, setTab] = useState('insured');
  const [groupCode, setGroupCode] = useState('');
  useEffect(() => {
    if (!open) { setTab('insured'); return undefined; }
    if (!companyTarget?.code || targetType(companyTarget) === 'group') { setGroupCode(''); return undefined; }
    let alive = true;
    apiClient.get(`/api/departments?company_code=${encodeURIComponent(companyTarget.code)}&kind=freelancer`)
      .then((r) => { if (alive) setGroupCode(r.ok && r.data?.group_code ? String(r.data.group_code) : ''); })
      .catch(() => { if (alive) setGroupCode(''); });
    return () => { alive = false; };
  }, [open, companyTarget]);
  const target = useMemo(
    () => (tab === 'freelance' && groupCode ? { ...companyTarget, code: groupCode, target_type: 'group' } : companyTarget),
    [tab, groupCode, companyTarget],
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [taskOptions, setTaskOptions] = useState([]);
  const [featureOptions, setFeatureOptions] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const selectedSet = useSelectedSet(selectedCodes);

  const loadDefaults = useCallback(async (departmentName = '') => {
    if (!target?.code) return;
    setLoading(true);
    try {
      const result = await apiClient.get(buildDefaultsUrl(target, departmentName));
      if (!result.ok || !result.data?.success) {
        showToast?.(result.data?.message || '초기 배정값을 불러오지 못했습니다.', 'error');
        return;
      }
      const nextDepartments = result.data.departments || [];
      setDepartments(nextDepartments);
      setSelectedDepartment(result.data.department_name || nextDepartments[0]?.name || '');
      setTaskOptions(result.data.task_options || []);
      setFeatureOptions(result.data.feature_options || []);
      setSelectedCodes(result.data.selected_perm_codes || []);
    } catch (_error) {
      showToast?.('초기 배정값을 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [target, showToast]);

  useEffect(() => {
    if (!open || !target?.code) return;
    setDepartments([]);
    setSelectedDepartment('');
    setTaskOptions([]);
    setFeatureOptions([]);
    setSelectedCodes([]);
    loadDefaults('');
  }, [open, target?.code, loadDefaults]);

  function toggle(code) {
    setSelectedCodes((prev) => {
      const set = new Set(prev);
      if (set.has(code)) set.delete(code);
      else set.add(code);
      return Array.from(set);
    });
  }

  async function save() {
    if (!target?.code || !selectedDepartment) return;
    setSaving(true);
    const result = await apiClient.put('/api/admin/initial-assignment-defaults', {
      target_type: targetType(target),
      target_code: target.code,
      department_name: selectedDepartment,
      perm_codes: selectedCodes,
    });
    setSaving(false);
    if (!result.ok || !result.data?.success) {
      showToast?.(result.data?.message || '초기 배정값 저장에 실패했습니다.', 'error');
      return;
    }
    showToast?.(`${selectedDepartment} 초기 배정값을 저장했습니다.`, 'success');
    loadDefaults(selectedDepartment);
  }

  function renderSection(title, items) {
    return (
      <section style={sectionStyle}>
        <div style={sectionHeadStyle}>
          <h3 style={sectionTitleStyle}>{title}</h3>
          <span style={countStyle}>
            {items.filter((item) => selectedSet.has(item.code)).length} / {items.length}
          </span>
        </div>
        <div style={optionGridStyle}>
          {items.map((item) => (
            <label key={item.code} style={optionStyle}>
              <input
                type="checkbox"
                checked={selectedSet.has(item.code)}
                onChange={() => toggle(item.code)}
              />
              <span>{item.label || item.code}</span>
            </label>
          ))}
        </div>
      </section>
    );
  }

  return (
    <Modal
      open={open}
      title={`${targetName(target)} 초기 배정값 설정`}
      icon="📋"
      onClose={onClose}
      width="min(980px, 94vw)"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={btn} onClick={onClose}>닫기</button>
          <button
            style={primaryBtn}
            disabled={saving || loading || !selectedDepartment}
            onClick={save}
          >
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      }
    >
      <div style={wrapStyle}>
        {groupCode && (
          <div style={tabGroupStyle} role="group" aria-label="구분">
            <button type="button" style={tabBtn(tab === 'insured')} onClick={() => setTab('insured')}>4대보험</button>
            <button type="button" style={tabBtn(tab === 'freelance')} onClick={() => setTab('freelance')}>3.3%</button>
          </div>
        )}
        <p style={noticeStyle}>
          부서별 초기 배정값은 새 직원을 등록하는 순간에만 한 번 적용됩니다.
          등록 후 부서를 바꿔도 기존 업무/기능 배정은 자동으로 바뀌지 않습니다.
          {tab === 'freelance' && groupCode && ' 3.3% 값은 이 테넌트의 모든 회사에서 3.3% 인원을 등록할 때 똑같이 붙습니다.'}
        </p>

        {loading && departments.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>불러오는 중...</p>
        ) : (
          <div style={bodyStyle}>
            <aside style={departmentPanelStyle}>
              <h3 style={panelTitleStyle}>부서 선택</h3>
              {departments.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>
                  등록된 부서가 없습니다.
                </p>
              ) : departments.map((department) => (
                <button
                  key={department.name}
                  type="button"
                  style={department.name === selectedDepartment
                    ? selectedDepartmentButtonStyle
                    : departmentButtonStyle}
                  onClick={() => loadDefaults(department.name)}
                >
                  {department.name}
                </button>
              ))}
            </aside>

            <div style={contentStyle}>
              {loading ? (
                <p style={{ margin: 0, color: 'var(--text-muted)' }}>불러오는 중...</p>
              ) : (
                <>
                  {renderSection('업무', taskOptions)}
                  {renderSection('기능', featureOptions)}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
